import * as amplitude from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import {
  DEVELOPMENT_HOSTNAMES,
  PRODUCTION_HOSTNAME,
  PROJECT_ID_IN_PATH,
} from "./constants";
import type { AnalyticsEnvironment, IdentifyUser, TrackEvent } from "./types";

const getEnvironment = (): AnalyticsEnvironment => {
  const hostname = window.location.hostname;

  if (DEVELOPMENT_HOSTNAMES.includes(hostname)) {
    return "development";
  }

  if (hostname === PRODUCTION_HOSTNAME) {
    return "production";
  }

  // Every other host (stage-app.monco.ai, preview apps) is kept out of production statistics
  return "staging";
};

const environment = getEnvironment();

// Localhost sends nothing unless a developer opts in to test events against Amplitude
const isTrackingDisabled =
  environment === "development" &&
  process.env.REACT_APP_AMPLITUDE_SEND_FROM_LOCALHOST !== "true";

let isInitialized = false;
let identifiedOrganizationId: string | undefined;

const readProjectIdFromPath = (): string | undefined =>
  PROJECT_ID_IN_PATH.exec(window.location.pathname)?.[1];

export const initAnalytics = (): void => {
  if (isInitialized) return;

  if (isTrackingDisabled) {
    console.log("📊 Amplitude Analytics disabled in development environment");
    return;
  }

  const amplitudeApiKey = process.env.REACT_APP_AMPLITUDE_API_KEY;
  if (amplitudeApiKey === undefined || amplitudeApiKey.trim() === "") {
    console.warn("Amplitude Analytics disabled: API key is not set");
    return;
  }

  try {
    amplitude.add(sessionReplayPlugin({ sampleRate: 1 }));

    amplitude.init(amplitudeApiKey.trim(), {
      autocapture: {
        elementInteractions: true,
      },
      defaultTracking: {
        sessions: true,
        pageViews: true,
        formInteractions: true,
        fileDownloads: true,
      },
    });

    const identifyEvent = new amplitude.Identify();
    identifyEvent.set("environment", environment);
    amplitude.identify(identifyEvent);

    isInitialized = true;
    console.log(`Amplitude Analytics initialized (${environment} environment)`);
  } catch (error) {
    console.error("Failed to initialize Amplitude:", error);
  }
};

export const identifyUser: IdentifyUser = ({ userId, userProperties }) => {
  if (isTrackingDisabled) {
    console.log("📊 User Identify (dev only):", userId, userProperties);
    return;
  }

  if (!isInitialized) {
    console.warn("Amplitude not initialized");
    return;
  }

  try {
    const { organizationId } = userProperties;
    identifiedOrganizationId =
      organizationId === undefined || organizationId === null
        ? undefined
        : String(organizationId);

    amplitude.setUserId(`user_${userId}`);

    if (Object.keys(userProperties).length === 0) return;

    const identifyEvent = new amplitude.Identify();
    Object.entries(userProperties).forEach(([propertyName, propertyValue]) => {
      // Amplitude ignores null/undefined values anyway; skipping them keeps the types valid
      if (propertyValue === undefined || propertyValue === null) return;
      identifyEvent.set(propertyName, propertyValue);
    });
    amplitude.identify(identifyEvent);
  } catch (error) {
    console.error("Failed to identify user:", error);
  }
};

// Analytics must never break the action it is attached to, so nothing here may throw
export const trackEvent: TrackEvent = (
  eventName,
  ...eventPropertiesArgument
) => {
  try {
    const [eventProperties] = eventPropertiesArgument;

    if (isTrackingDisabled) {
      console.log("📊 Analytics Event (dev only):", eventName, eventProperties);
      return;
    }

    if (!isInitialized) {
      console.warn("Amplitude not initialized");
      return;
    }

    const explicitProjectId = eventProperties?.projectId;
    const projectId =
      explicitProjectId === undefined || explicitProjectId === null
        ? readProjectIdFromPath()
        : String(explicitProjectId);

    amplitude.track(eventName, {
      ...eventProperties,
      environment,
      ...(identifiedOrganizationId !== undefined && {
        organizationId: identifiedOrganizationId,
      }),
      ...(projectId !== undefined && { projectId }),
    });
  } catch (error) {
    console.error(`Failed to track event "${eventName}":`, error);
  }
};

export const resetUser = (): void => {
  if (isTrackingDisabled) {
    console.log("📊 User Reset (dev only)");
    return;
  }

  if (!isInitialized) return;

  try {
    identifiedOrganizationId = undefined;
    amplitude.reset();
  } catch (error) {
    console.error("Failed to reset user:", error);
  }
};
